import React, { useCallback, useMemo, memo } from 'react'
import useRowHydration from '../../../../../services/community/identity/library/useRowHydration'
import CustomMenu from '../../Library/ProfilePlaylistMenuOptions/CustomMenu'
import PlaylistShareButton from '../../../shared/PlaylistShareButton/index'
import PlaylistSeeEntireLink from '../../Library/PlaylistSeeEntireLink/index'
import PlaylistSharingDropdown, { VISIBILITY, isPlaylistVisibilityLevel }
  from '../../Library/PlaylistTabs/MyPlaylistsTab/PlaylistSharingDropdown/index'
import PlaylistOutline from '../../../../Icon/icons/PlaylistOutline/index'
import Clock from '../../../../Icon/icons/Clock/index'
import CommunityEmptyState from '../CommunityEmptyState/CommunityEmptyState'
import type { EditToolbarLabels } from '../CommunityProfileCarousels/index'
import type {
  PlaylistShell, PlaylistVisibilityLevel, EnrichedPlaylistItem,
} from '../../../../../services/community/types'
import type { PlaylistRowContextValue } from './PlaylistRowContext'
import PlaylistRow from './PlaylistRow'

/**
 * Variant: custom user playlist, non-editing.
 *
 * Wraps the new `useRowHydration` per-row hook + slot wiring. All
 * labels arrive as already-resolved strings — the variant deliberately
 * doesn't subscribe to `StaticTextContext` so `React.memo` short-
 * circuits cleanly when only sibling rows change.
 *
 * §10B Option 1: drops `'share'` from the menu's `allowedActions` on
 * populated rows because the right-side `<PlaylistShareButton>` is
 * already visible. Empty rows expose only `['edit', 'delete']`.
 */

export type MyPlaylistRowAction = (
  playlistId: string,
  action: string,
  playlistType?: string,
  playlistName?: string,
) => void

export interface MyPlaylistRowEmptyStateLabels {
  callout: string
  whatNext: string
  browseGaiaLibrary: string
}

export interface MyPlaylistRowProps {
  playlist: PlaylistShell
  language: string
  jwt?: string
  rowAuthorDisplay?: string | null
  allowedVisibilities?: readonly PlaylistVisibilityLevel[]
  onAction: MyPlaylistRowAction
  onUpdateVisibility?: (id: string, vis: PlaylistVisibilityLevel) => void
  onWhatNextClick: (prompt: string) => void
  onBrowseClick: () => void
  /** Pre-resolved labels — pass-through, not consumed via context. */
  seeMoreLabel: string
  showLessLabel?: string
  seeEntireTemplate: string
  videosLabel: string
  zeroDurationLabel: string
  editToolbarLabels?: EditToolbarLabels
  emptyStateLabels: MyPlaylistRowEmptyStateLabels
}

function MyPlaylistRowImpl ({
  playlist,
  language,
  jwt,
  rowAuthorDisplay = null,
  allowedVisibilities,
  onAction,
  onUpdateVisibility,
  onWhatNextClick,
  onBrowseClick,
  seeMoreLabel,
  showLessLabel,
  seeEntireTemplate,
  videosLabel,
  zeroDurationLabel,
  editToolbarLabels,
  emptyStateLabels,
}: MyPlaylistRowProps) {
  const { details, isHydrating } = useRowHydration(playlist, language, { jwt })
  const expectedItemCount = playlist.itemCount ?? 0
  const isEmpty = !isHydrating && details.items.length === 0
  const visibility: PlaylistVisibilityLevel = isPlaylistVisibilityLevel(playlist.visibility)
    ? playlist.visibility
    : VISIBILITY.PRIVATE

  const ctx = useMemo<PlaylistRowContextValue>(() => ({
    playlistId: playlist.id,
    playlistName: playlist.playlistName || '',
    playlistType: playlist.playlistType || undefined,
    items: details.items,
    expectedItemCount,
    totalDuration: details.totalDuration ?? null,
    primaryTopic: details.primaryTopic,
    isHydrating,
    isEditing: false,
  }), [
    playlist.id, playlist.playlistName, playlist.playlistType,
    details.items, details.totalDuration, details.primaryTopic,
    expectedItemCount, isHydrating,
  ])

  const handleChangeVisibility = useCallback((vis: PlaylistVisibilityLevel) => {
    if (onUpdateVisibility) onUpdateVisibility(playlist.id, vis)
  }, [onUpdateVisibility, playlist.id])

  // Zero-state stats — `CommunityPlaylistRowHeaderBase` collapses count + duration
  // when their values are zero. Provide an explicit "0 Videos · 0h 00m" node so the
  // row doesn't visually shrink. Markup mirrors the legacy inline version verbatim
  // (playlist-row-header__stats outer, --episodes modifier on the count slot) —
  // shared SCSS in `_CommunityPlaylistRowHeader.scss` styles those classes.
  const emptyStats = useMemo(() => (isEmpty ? (
    <div className="playlist-row-header__stats">
      <span className="playlist-row-header__stat playlist-row-header__stat--episodes">
        <PlaylistOutline aria-hidden="true" />
        {videosLabel}
      </span>
      <span className="playlist-row-header__stat">
        <Clock aria-hidden="true" />
        {zeroDurationLabel}
      </span>
    </div>
  ) : undefined), [isEmpty, videosLabel, zeroDurationLabel])

  // Hoist allowedActions through useMemo so the `<CustomMenu>` element below
  // gets a stable `allowedActions` ref. Without this, the literal array is a
  // fresh reference per render → `<CustomMenu>` props change → its
  // `React.memo` shallow-equal fails → CustomMenu and its
  // `useDisclosure`/`useClickOutside` hooks re-fire on every parent re-render.
  // Same rationale governs the memoised header slots below.
  const allowedActions = useMemo<Array<'edit' | 'share' | 'copyLink' | 'delete'>>(() => (
    isEmpty ? ['edit', 'delete'] : ['edit', 'copyLink', 'delete']
  ), [isEmpty])

  // Memoise the three header slots passed to `<PlaylistRow.Header>`. The
  // downstream `CommunityPlaylistRowHeader` is `React.memo`'d on its props,
  // and `nameAction` / `statusTag` / `shareButton` are forwarded verbatim,
  // so fresh ReactElement refs here defeat the memo and ripple into
  // `PlaylistShareButton` / `usePreloadImages` / `CustomMenu` re-renders.
  // Per-slot `useMemo` with the narrowest dep set keeps the refs stable
  // through hydration-driven row re-renders.
  const headerStatusTag = useMemo(() => (isEmpty ? null : (
    <PlaylistSharingDropdown
      visibility={visibility}
      onChangeVisibility={handleChangeVisibility}
      allowedVisibilities={
        allowedVisibilities as readonly PlaylistVisibilityLevel[] | undefined
      }
      followerCount={playlist.followerCount || 0}
    />
  )), [
    isEmpty, visibility, handleChangeVisibility, allowedVisibilities, playlist.followerCount,
  ])

  const headerShareButton = useMemo(() => (isEmpty ? null : (
    <PlaylistShareButton
      playlistId={playlist.id}
      playlistName={playlist.playlistName || ''}
      description={playlist.description}
      items={details.items as EnrichedPlaylistItem[]}
    />
  )), [
    isEmpty, playlist.id, playlist.playlistName, playlist.description, details.items,
  ])

  const headerNameAction = useMemo(() => (
    <CustomMenu
      playlistId={playlist.id}
      playlistType={playlist.playlistType}
      playlistName={playlist.playlistName}
      allowedActions={allowedActions}
      onAction={onAction}
    />
  ), [playlist.id, playlist.playlistType, playlist.playlistName, allowedActions, onAction])

  // Same rationale for the empty-state slot — `PlaylistRow.Body` re-runs on
  // every row commit (compound facade, not memo'd by design), so the slot
  // node would otherwise have a fresh identity every render. It's only
  // rendered when items.length === 0, but Body forwards it as a prop
  // regardless, so identity matters for downstream comparisons.
  const emptyStateNode = useMemo(() => (
    <CommunityEmptyState
      variant="compact"
      description={emptyStateLabels.callout}
      primaryCta={{
        label: emptyStateLabels.whatNext,
        onClick: onWhatNextClick,
      }}
      secondaryCta={{
        label: emptyStateLabels.browseGaiaLibrary,
        onClick: onBrowseClick,
      }}
    />
  ), [
    emptyStateLabels.callout, emptyStateLabels.whatNext, emptyStateLabels.browseGaiaLibrary,
    onWhatNextClick, onBrowseClick,
  ])

  // Non-editing variant: no remove handler wired. Edit-mode use cases route through
  // EditableMyPlaylistRow which owns the remove/reorder callbacks.
  const videoCount = expectedItemCount || details.items.length
  const seeEntireLabel = seeEntireTemplate.replace('{count}', String(videoCount))

  return (
    <PlaylistRow.Provider value={ctx}>
      <PlaylistRow.Frame className="profile-my-playlists__playlist-row">
        <PlaylistRow.Header
          authorDisplay={rowAuthorDisplay}
          followerCount={playlist.followerCount || 0}
          statusTag={headerStatusTag}
          shareButton={headerShareButton}
          customStats={emptyStats}
          nameAction={headerNameAction}
        />
        <PlaylistRow.Body
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
          hideToggle
          editToolbarLabels={editToolbarLabels}
          emptyState={emptyStateNode}
        />
        {/*
          Footer slot — three states:
            (a) items resolved: real link.
            (b) hydration in flight with a non-empty shell: invisible
                placeholder so the row's height doesn't grow when items
                arrive (CLS fix — the divider + padding + line-box reserve
                the eventual ~45px footprint).
            (c) genuinely empty (`!isHydrating && items.length === 0`):
                no footer. The empty-state CTAs below own the vertical
                space — adding a dangling divider would look broken.
        */}
        {(() => {
          if (details.items.length > 0) {
            return (
              <PlaylistSeeEntireLink
                to={`/playlist/${playlist.id}`}
                videoCount={videoCount}
                label={seeEntireLabel}
              />
            )
          }
          if (isHydrating && expectedItemCount > 0) {
            // Pass the real label/count so the transparent placeholder wraps
            // exactly the way the resolved link will — see the docblock on
            // PlaylistSeeEntireLink's placeholder branch.
            return (
              <PlaylistSeeEntireLink
                to=""
                videoCount={videoCount}
                label={seeEntireLabel}
                placeholder
              />
            )
          }
          return null
        })()}
      </PlaylistRow.Frame>
    </PlaylistRow.Provider>
  )
}

const MyPlaylistRow = memo(MyPlaylistRowImpl)
MyPlaylistRow.displayName = 'MyPlaylistRow'
export default MyPlaylistRow
